import { Immutable } from "immer";

import { Products } from "../../models/catalogue";
import { isoProductCategoryID } from "../../models/nominals";
import { check } from "../../models/utils";
import { type AppDispatch } from "../../store/reducers";
import { applyHistoryUpdate, updateSortMethod } from "./reducers";
import { IReduxState } from "./reducers.interfaces";
import { loadProducts } from "./thunks";

type ISSRData = Immutable<{
    products_data: string;
    initial_filter_state: string;
    initial_sort_method: string;
}>;

/**
 * Dispatch fulfilled thunk actions to pre-populate the Redux store with
 * SSR-fetched data for the ProductGrid component.
 *
 * Follows the same pattern as prefillSSRStoreData in reviews/ssr.ts.
 */
export const prefillSSRProductGridData = (
    dispatch: AppDispatch,
    data: ISSRData,
): void => {
    // 1. Parse and decode products through the Products io-ts codec
    // (handles API → internal model conversion automatically).
    // On failure, bail out — the client will fetch via loadProducts thunk.
    let products;
    try {
        products = check(Products.decode(JSON.parse(data.products_data)));
    } catch (e) {
        console.error("Failed to parse SSR products_data:", e);
        return;
    }

    // 2. Filter to available products (same logic as loadProducts thunk)
    const availableProducts = products.filter((product) => {
        const availableParent = product.availability.is_available_to_buy;
        const availableChild = product.children.find(
            (child) => child.availability.is_available_to_buy,
        );
        return availableParent || availableChild;
    });

    // 3. Dispatch loadProducts.fulfilled() to prefill product state
    dispatch(
        loadProducts.fulfilled(
            availableProducts,
            "",
            isoProductCategoryID.wrap(0),
        ),
    );

    // 4. Apply initial filter state from URL params
    try {
        const filterState: IReduxState["filters"] = JSON.parse(
            data.initial_filter_state,
        );
        if (Object.keys(filterState).length > 0) {
            dispatch(applyHistoryUpdate(filterState));
        }
    } catch (e) {
        console.error("Failed to parse SSR initial_filter_state:", e);
    }

    // 5. Apply initial sort method from URL
    if (data.initial_sort_method) {
        dispatch(updateSortMethod(data.initial_sort_method));
    }
};
